import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { inbox, type tickets } from '@repletoryreach/sdk-api';
import type { ConversationMessage } from '@repletoryreach/sdk-api/inbox';
import { Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { useT, type Translate } from '../../../i18n/I18nProvider';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { channelLabel } from '../lib/data';

type Filter = 'all' | 'messages' | 'notes' | 'changes';

type Entry =
  | { key: string; at: number; kind: 'activity'; activity: tickets.Activity }
  | { key: string; at: number; kind: 'message'; message: ConversationMessage; channel: string | null };

const CONVERSATIONAL = new Set(['reply', 'client_message']);

/** What a change was, in words, when the service sent no sentence of its own. */
function activityText(t: Translate, a: tickets.Activity): string {
  if (a.body?.trim()) return a.body;
  if (typeof a.detail === 'string' && a.detail.trim()) return a.detail;
  return t(`tickets.activity.${a.kind}`, a.kind);
}

/**
 * The ticket's story, oldest first: its own activity merged with the messages of every linked conversation,
 * each labelled by its channel. While the conversations have loaded, their messages stand in for the ticket's
 * own reply and client-message entries, so nothing shows twice.
 */
export function Timeline({ ticket }: { ticket: tickets.Ticket }) {
  const t = useT();
  const [filter, setFilter] = useState<Filter>('all');

  const convs = useQueries({
    queries: ticket.conversations.map((c) => ({
      // The inbox's own key and call, so the two pages share what they have read.
      queryKey: ['conversation', c.id, 'messages'],
      queryFn: () => inbox.listMessages(c.id, { limit: 100 }),
      refetchInterval: 30_000
    }))
  });
  const allLoaded = convs.length > 0 && convs.every((q) => q.isSuccess);
  const failed = convs.find((q) => q.error);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];
    for (const a of ticket.activities ?? []) {
      if (allLoaded && CONVERSATIONAL.has(a.kind)) continue;
      out.push({ key: `a-${a.id}`, at: Date.parse(a.at), kind: 'activity', activity: a });
    }
    ticket.conversations.forEach((c, i) => {
      for (const m of convs[i]?.data?.data ?? []) {
        out.push({ key: `m-${c.id}-${m.id}`, at: Date.parse(m.created_at), kind: 'message', message: m, channel: c.channel });
      }
    });
    return out.sort((a, b) => a.at - b.at);
  }, [ticket.activities, ticket.conversations, convs, allLoaded]);

  const visible = entries.filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'messages') return e.kind === 'message' || CONVERSATIONAL.has(e.activity.kind);
    if (filter === 'notes') return e.kind === 'activity' && e.activity.kind === 'note';
    return e.kind === 'activity' && e.activity.kind !== 'note' && !CONVERSATIONAL.has(e.activity.kind);
  });

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group gap="sm" justify="space-between" wrap="wrap">
          <Text role="subtitle" fw={600}>{t('tickets.timeline.title')}</Text>
          <SegmentedControl
            size="xs"
            value={filter}
            onChange={(v) => setFilter(v as Filter)}
            data={[
              { value: 'all', label: t('tickets.timeline.all') },
              { value: 'messages', label: t('tickets.timeline.messages') },
              { value: 'notes', label: t('tickets.timeline.notes') },
              { value: 'changes', label: t('tickets.timeline.changes') }
            ]}
          />
        </Group>

        {failed?.error && (
          <Alert color="warning" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{t('tickets.timeline.messagesFailed')}</span>
              <Button size="xs" variant="default" onClick={() => convs.forEach((q) => void q.refetch())}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        {visible.length === 0 ? (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('tickets.timeline.empty')}</Text>
        ) : (
          <ol className="rr-tickets__timeline" data-testid="ticket-timeline">
            {visible.map((e) => (e.kind === 'message'
              ? <MessageEntry key={e.key} m={e.message} channel={e.channel} />
              : <ActivityEntry key={e.key} a={e.activity} />))}
          </ol>
        )}
      </Stack>
    </Card>
  );
}

function MessageEntry({ m, channel }: { m: ConversationMessage; channel: string | null }) {
  const t = useT();
  const inbound = m.direction === 'inbound';
  const text = m.body?.trim()
    || (m.template_name ? t('tickets.timeline.template', { name: m.template_name }) : '')
    || (m.media_name ? t('tickets.timeline.attachment', { name: m.media_name }) : t('tickets.timeline.media'));
  return (
    <li className={`rr-tickets__entry rr-tickets__bubble ${inbound ? 'is-in' : 'is-out'}${m.failed_at ? ' is-failed' : ''}`}>
      <div className="rr-tickets__bubble-head">
        <span className="rr-tickets__channel">{channelLabel(t, channel)}</span>
        <span>{inbound ? t('tickets.timeline.fromClient') : t('tickets.timeline.toClient')}</span>
        <time dateTime={m.created_at} title={formatDateTime(m.created_at)}>{formatRelative(m.created_at)}</time>
      </div>
      <div className="rr-tickets__bubble-body">{text}</div>
      {m.failed_at && <div className="rr-tickets__bubble-error">{m.error_title || t('tickets.timeline.notDelivered')}</div>}
    </li>
  );
}

function ActivityEntry({ a }: { a: tickets.Activity }) {
  const t = useT();
  const when = <time dateTime={a.at} title={formatDateTime(a.at)}>{formatRelative(a.at)}</time>;
  if (a.kind === 'note') {
    return (
      <li className="rr-tickets__entry rr-tickets__note">
        <div className="rr-tickets__bubble-head">
          <span className="rr-tickets__note-tag">{t('tickets.timeline.note')}</span>
          <span>{a.actor || t('tickets.timeline.someone')}</span>
          {when}
        </div>
        <div className="rr-tickets__bubble-body">{a.body}</div>
      </li>
    );
  }
  if (CONVERSATIONAL.has(a.kind)) {
    const inbound = a.kind === 'client_message';
    return (
      <li className={`rr-tickets__entry rr-tickets__bubble ${inbound ? 'is-in' : 'is-out'}`}>
        <div className="rr-tickets__bubble-head">
          <span>{inbound ? t('tickets.timeline.fromClient') : (a.actor || t('tickets.timeline.toClient'))}</span>
          {when}
        </div>
        <div className="rr-tickets__bubble-body">{a.body}</div>
      </li>
    );
  }
  return (
    <li className={`rr-tickets__entry rr-tickets__change is-${a.kind}`}>
      <span className="rr-tickets__change-dot" aria-hidden />
      <span className="rr-tickets__change-text">
        <strong>{a.actor || t('tickets.timeline.system')}</strong> {activityText(t, a)}
      </span>
      {when}
    </li>
  );
}
